import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  CircularProgress,
  Button,
  Container,
  Typography,
  Tabs,
  Tab,
  Grid,
  TextField,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
} from '@mui/material';
import Page from '../components/Page';
import AddHeirDialog from '../sections/Deceased/AddHeirDialog';
import AddConventionalPropertyDialog from '../sections/Deceased/AddConventionalPropertyDialog';
import AddSectionalPropertyDialog from '../sections/Deceased/AddSectionalPropertyDialog';
import AddMovableAssetDialog from '../sections/Deceased/AddMovableAssetDialog';
import AddSharesAssetDialog from '../sections/Deceased/AddSharesAssetDialog';
import AddLiabilityDialog from '../sections/Deceased/AddLiabilityDialog';
import AddClaimDialog from '../sections/Deceased/AddClaimDialog';
import AddPolicyDialog from '../sections/Deceased/AddPolicyDialog';
import AddBondOfSecurityDialog from '../sections/Deceased/AddBondOfSecurityDialog';
// --- HELPER COMPONENTS ---

// Formats a monetary value as "R1,000,000.00" (thousands separators + 2 decimals, always)
const formatCurrency = (value) => {
  const num = parseFloat(value || 0);
  return `R ${(isNaN(num) ? 0 : num).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// A TabPanel to handle showing/hiding content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 3, minHeight: '600px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// A standardized row for "Label | Input" layout
// customGrid allows you to pass specific column widths if the standard 4/8 split isn't right
const FormRow = ({ label, children, customGrid }) => {
  const labelCols = customGrid ? customGrid[0] : 12;
  const inputCols = customGrid ? customGrid[1] : 12;
  const mdLabel = customGrid ? customGrid[2] : 4;
  const mdInput = customGrid ? customGrid[3] : 8;

  return (
    <Grid container spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      <Grid item xs={labelCols} md={mdLabel}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={inputCols} md={mdInput}>
        {children}
      </Grid>
    </Grid>
  );
};

export default function DeceasedEstatePage() {
  const { id } = useParams();
  const [matter, setMatter] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openHeirDialog, setOpenHeirDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openMovableTypeDialog, setOpenMovableTypeDialog] = useState(false);
const [openMovableDetails, setOpenMovableDetails] = useState(false);
const handleOpenMovableTypeDialog = () => setOpenMovableTypeDialog(true);
const handleCloseMovableTypeDialog = () => setOpenMovableTypeDialog(false);
const [openSharesDetails, setOpenSharesDetails] = useState(false);
const [openLiabilityDialog, setOpenLiabilityDialog] = useState(false);
const [openClaimDialog, setOpenClaimDialog] = useState(false);
const [openPolicyDialog, setOpenPolicyDialog] = useState(false);
const [openBondDialog, setOpenBondDialog] = useState(false);
const [openSnackbar, setOpenSnackbar] = useState(false);
const [snackbarMessage, setSnackbarMessage] = useState('');
const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleOpenHeirDialog = () => {
    setEditData(null);
    setOpenHeirDialog(true);
  };

 const [openConventionalDialog, setOpenConventionalDialog] = useState(false);

  const handleOpenConventionalDialog = () => {
    setOpenPropertyTypeDialog(false); // Close the small selection modal
    setEditData(null);
    setOpenConventionalDialog(true);  // Open the large modal
  };

  // State for the small "Property Type" selection modal
  const [openPropertyTypeDialog, setOpenPropertyTypeDialog] = useState(false);

  const handleOpenPropertyTypeDialog = () => {
    setOpenPropertyTypeDialog(true);
  };

  const handleClosePropertyTypeDialog = () => {
    setOpenPropertyTypeDialog(false);
  };

  const [openSectionalDialog, setOpenSectionalDialog] = useState(false);

const handleOpenSectionalDialog = () => {
  setOpenPropertyTypeDialog(false);
  setEditData(null);
  setOpenSectionalDialog(true);
};



const handleOpenMovableDetails = () => {
    setOpenMovableTypeDialog(false);
    setEditData(null);
    setOpenMovableDetails(true);
};
const handleOpenSharesDetails = () => {
        setOpenMovableTypeDialog(false); // Close the selection prompt
        setEditData(null);
        setOpenSharesDetails(true);      // Open the Shares details modal
};

const [openEntryTypeDialog, setOpenEntryTypeDialog] = React.useState(false);

const handleOpenEntryTypeDialog = () => setOpenEntryTypeDialog(true);
const handleCloseEntryTypeDialog = () => setOpenEntryTypeDialog(false);

// Handler for 'Claim in favour' button
const handleClaimInFavourSelected = () => {
    handleCloseEntryTypeDialog();
    setEditData(null);
    setOpenClaimDialog(true);
};

// Handler for 'Policy' button
const handlePolicySelected = () => {
    handleCloseEntryTypeDialog();
    setEditData(null);
    setOpenPolicyDialog(true);
};

  const handleEditItem = (item, type) => {
    setEditData(item);
    switch(type) {
      case 'heir': setOpenHeirDialog(true); break;
      case 'conventional': setOpenConventionalDialog(true); break;
      case 'sectional': setOpenSectionalDialog(true); break;
      case 'movable': setOpenMovableDetails(true); break;
      case 'shares': setOpenSharesDetails(true); break;
      case 'claim': setOpenClaimDialog(true); break;
      case 'policy': setOpenPolicyDialog(true); break;
      case 'liability': setOpenLiabilityDialog(true); break;
      case 'bond': setOpenBondDialog(true); break;
      default: break;
    }
  };

const handleCloseSnackbar = (event, reason) => {
  if (reason === 'clickaway') return;
  setOpenSnackbar(false);
};

const fetchAllData = useCallback(async () => {
  try {
    const [matterRes, estateRes] = await Promise.all([
      axios.get(`https://demo.insollex.com/api/legal-matter/${id}`),
      axios.get(`https://demo.insollex.com/api/deceased-estate/${id}`).catch(() => ({ data: {} }))
    ]);

    setMatter(matterRes.data);
    setLoading(false);

    setFormData({
      ...matterRes.data,
      ...estateRes.data,
      estateNo: matterRes.data.estateNo,
      matterName: matterRes.data.matterName,
      // 🛠️ FIX: Ensure shared fields are populated from LegalMatter (the source of truth for these)
      magistrateOffice: matterRes.data.magistrateOffice,
      bankName: matterRes.data.bankName,
      bankAccountNo: matterRes.data.bankAccountNo,
    });
  } catch (error) {
    console.error("Fetch error:", error);
    setLoading(false);
  }
}, [id]);

const handleRefetch = () => { 
    fetchAllData();
    setSnackbarMessage("Saved successfully");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
};

  // Tab Definitions
  const tabs = [
    "1. General", "2. Executor", "3. Executier (Trust)", "4. Co-executor",
    "5. Co-executor (Trust)", "6. Beneficiaries", "7. Deceased", "8. Heirs",
    "9. Immovables", "10. Movables", "11. Comm (Partners)", "12. Liabilities", "13. Bond of security"
  ];

  useEffect(() => {
    if (id) fetchAllData();
  }, [id, fetchAllData]);

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value,
  }));
};

  const handleSave = async () => {
      setSaving(true);
      try {
          // 1. Send the update to the backend
          // 🛠️ FIX: Use LegalMatter endpoint to handle both entity updates correctly and prevent data loss
          const { id: _id, ...payload } = formData;
          await axios.put(`https://demo.insollex.com/api/legal-matter/${id}`, payload);
          
          // ALSO hit the Deceased Estate endpoint to ensure extended JSON fields map safely
          await axios.put(`https://demo.insollex.com/api/deceased-estate/${id}`, payload).catch(err => {
              console.warn("Deceased estate specific endpoint fallback:", err);
          });
          
          setSnackbarMessage("Changes saved successfully");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);

          // 2. FIXED: Correctly call the available refresh function
          await fetchAllData();

      } catch (error) {
          console.error("Error saving:", error);
          // 3. Improve error visibility
          const errorMsg = error.response?.data?.error || "Failed to save changes";
          setSnackbarMessage(errorMsg);
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
      } finally {
          setSaving(false);
      }
  };
  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleDelete = async (subPath, itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`https://demo.insollex.com/api/legal-matter/${id}/${subPath}/${itemId}`);
      setSnackbarMessage("Item deleted successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchAllData();
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbarMessage("Failed to delete item");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} />;
  if (!matter) return <Typography variant="h6" align="center" sx={{ mt: 5 }}>Matter not found</Typography>;

  return (
    <Page title="Deceased Estate">
      <Container maxWidth={false} sx={{ pb: 5 }}>
        {/* Header */}
        <Typography variant="h4" sx={{ mb: 3 }}>
          Deceased Estate: {matter.matterName}
        </Typography>

        {/* Tabs Header */}
        <Paper square elevation={0} variant="outlined" sx={{ borderBottom: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Paper>

        {/* Tabs Content Area */}
        <Paper square variant="outlined">
          
          {/* ================= TAB 1: GENERAL ================= */}
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={0.5}>
              
              {/* Complex Top Row: Ref | Evate Tags | Estate Tags */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={12} md={5}>
                    <FormRow label="Complete your internal file reference/account no." customGrid={[12, 12, 5, 7]}>
                        <TextField 
                          fullWidth size="small" variant="outlined" 
                          name="fileRef"
                          value={formData.fileRef || ''}
                          onChange={handleChange}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 } }} 
                        />
                    </FormRow>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Grid container alignItems="center">
                        <Grid item xs={4}><Typography variant="subtitle2" sx={{ pl: 1 }}>Estate tags</Typography></Grid>
                        <Grid item xs={8}>
                            <TextField 
                              fullWidth size="small" variant="outlined" 
                              name="estateTags" value={formData.estateTags || ''} onChange={handleChange}
                              sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                            />
                        </Grid>
                    </Grid>
                </Grid>
              </Grid>

              <FormRow label="Which Master's Office is dealing with Estate?">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="masterOffice" value={formData.masterOffice || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <FormRow label="Magistrate's Office">
                <TextField 
  name="magistrateOffice" 
  value={formData.magistrateOffice ?? ''} // FIX: Add ?? ''
  onChange={handleChange} 
  fullWidth size="small"
/>
              </FormRow>

              <FormRow label="Estate Estate (Name)">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="matterName" value={formData.matterName || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <FormRow label="Master's number/Master's Ref">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="estateNo" value={formData.estateNo || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <FormRow label="Which Division of the High Court - English?">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="highCourtEnglish" value={formData.highCourtEnglish || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <FormRow label="Which Division of the High Court - Afrikaans?">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="highCourtAfrikaans" value={formData.highCourtAfrikaans || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <FormRow label="Where will all the Documents be kept?">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="documentLocation" value={formData.documentLocation || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />

              {/* BANK SECTION */}
              <FormRow label="Name of Bank where Estate account is held">
                <TextField 
                  fullWidth size="small" variant="outlined" 
                  name="bankName" value={formData.bankName || ''} onChange={handleChange}
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                />
              </FormRow>

              <FormRow label="Branch Name">
                <TextField 
                  sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" 
                  name="branchName" value={formData.branchName || ''} onChange={handleChange}
                />
              </FormRow>

              <FormRow label="Branch Code">
                <TextField 
                  sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" 
                  name="branchCode" value={formData.branchCode || ''} onChange={handleChange}
                />
              </FormRow>

              <FormRow label="Bank Account Number of Account">
                <TextField 
                  sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" 
                  name="bankAccountNo" value={formData.bankAccountNo || ''} onChange={handleChange}
                />
              </FormRow>

              <FormRow label="Is this a joint estate?">
                 <Checkbox 
                   size="small" sx={{ p: 0 }} 
                   name="isJointEstate" checked={formData.isJointEstate || false} onChange={handleChange}
                 />
              </FormRow>

              <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />

              {/* DEATH NOTICE SIGNATORY */}
              <FormRow label="Name and address of person signing Death Notice">
                <TextField name="deathNoticeSignatoryNameAddress" value={formData.deathNoticeSignatoryNameAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" multiline rows={2} sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }} />
              </FormRow>

              <FormRow label="Capacity/relation of person signing Death Notice">
                <TextField name="deathNoticeSignatoryCapacity" value={formData.deathNoticeSignatoryCapacity || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
              </FormRow>

              <FormRow label="Was this signatory present at death?">
                <Checkbox name="signatoryPresentAtDeath" checked={formData.signatoryPresentAtDeath || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
              </FormRow>

              <FormRow label="If not, did he identify deceased after Death Notice?">
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Checkbox name="identifiedAfterDeathNotice" checked={formData.identifiedAfterDeathNotice || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Name and address of person identifying deceased
                    </Typography>
                    <TextField name="identifierNameAddress" value={formData.identifierNameAddress || ''} onChange={handleChange} size="small" sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} />
                </Stack>
              </FormRow>

              <FormRow label="Complete your reference present at death?">
                 <Stack direction="row" spacing={2} alignItems="center">
                   <RadioGroup row name="worksRefTerritoryLang" value={formData.worksRefTerritoryLang || 'English'} onChange={handleChange}>
                     <FormControlLabel value="English" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">English</Typography>} />
                     <FormControlLabel value="Afrikaans" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Afrikaans</Typography>} />
                   </RadioGroup>
                   <Typography variant="body2">Works ref territory</Typography>
                   <TextField name="worksRefTerritory" value={formData.worksRefTerritory || ''} onChange={handleChange} size="small" sx={{ width: '100px', '& .MuiInputBase-input': { py: 0.5 } }} />
                 </Stack>
              </FormRow>
              
              {/* Language Selection Row */}
               <FormRow label="Language links reference">
                 <RadioGroup row name="languageLinksRef" value={formData.languageLinksRef || 'English'} onChange={handleChange}>
                     <FormControlLabel value="English" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">English</Typography>} />
                     <FormControlLabel value="Afrikaans" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Afrikaans</Typography>} />
                   </RadioGroup>
               </FormRow>

              <FormRow label="Recipient's reference">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox name="hasRecipientRef" checked={formData.hasRecipientRef || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <TextField name="recipientRefDetails" value={formData.recipientRefDetails || ''} onChange={handleChange} fullWidth size="small" variant="outlined" placeholder="Reference details..." sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} />
                </Stack>
              </FormRow>

              <FormRow label="Will estate have multiple L&D accounts?">
                <RadioGroup row name="multipleLDAccounts" value={formData.multipleLDAccounts || 'Direct'} onChange={handleChange}>
                  <FormControlLabel value="Direct" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Direct</Typography>} />
                  <FormControlLabel value="Newspaper" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Via local newspaper</Typography>} />
                </RadioGroup>
              </FormRow>

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox name="showNominalValue" checked={formData.showNominalValue || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  Show nominal value of assets and liabilities as captured in some bank account.
                </Typography>
              </Box>

            </Stack>
          </TabPanel>

          {/* ================= TAB 2: EXECUTOR ================= */}
        <TabPanel value={tabValue} index={1}>
           <Stack spacing={0.5}>
            
            <FormRow label="Executor's Full Names" required>
              <TextField name="executorFullName" value={formData.executorFullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fffde7', '& .MuiInputBase-input': { py: 0.5 } }} />
            </FormRow>

            <FormRow label="Executor's Identity Number">
              <TextField name="executorID" value={formData.executorID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
            </FormRow>

            <FormRow label="Executor's Home Address (if applicable)">
              <TextField name="executorHomeAddress" value={formData.executorHomeAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
            </FormRow>

            <FormRow label="Home Tel. No.">
              <TextField name="executorHomePhone" value={formData.executorHomePhone || ''} onChange={handleChange} sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
            </FormRow>

            <FormRow label="Executor's Business Address">
              <TextField name="executorBusinessAddress" value={formData.executorBusinessAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
            </FormRow>

            <FormRow label="Executor/Agent's Bus Phone Number">
              <TextField name="executorBusinessPhone" value={formData.executorBusinessPhone || ''} onChange={handleChange} sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
            </FormRow>

            <FormRow label="Executor/Agent's Fax Number">
              <TextField name="executorFax" value={formData.executorFax || ''} onChange={handleChange} sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
            </FormRow>

            <FormRow label="Executor's Domicilium Address">
              <TextField name="executorDomicilium" value={formData.executorDomicilium || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
            </FormRow>

            <FormRow label="Executor's Agent's Full Names (if appl.)">
              <TextField name="executorAgentFullName" value={formData.executorAgentFullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
            </FormRow>

            <FormRow label="Executor's Agent's Address">
              <TextField name="executorAgentAddress" value={formData.executorAgentAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
            </FormRow>

            <FormRow label="Estate Number:">
              <TextField name="estateNo" sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} value={formData.estateNo || ''} onChange={handleChange} size="small" variant="outlined" />
            </FormRow>

            <FormRow label="Place Letters of Executorship issued">
              <TextField name="placeLettersIssued" value={formData.placeLettersIssued || ''} onChange={handleChange} sx={{ width: '500px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
            </FormRow>

            <FormRow label="Date of Letters of Executorship">
              <TextField name="dateLettersIssued" value={formData.dateLettersIssued || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" placeholder="YYYY-MM-DD" />
            </FormRow>

            <FormRow label="Executor's relationship to deceased">
              <TextField name="executorRelationship" value={formData.executorRelationship || ''} onChange={handleChange} sx={{ width: '100%', maxWidth: '600px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
            </FormRow>

            <Box sx={{ mt: 4 }}>
                <FormRow label="Will Executor sign the L&D obo the Co-Executor(s)?">
                   <Checkbox name="executorSignsLD" checked={formData.executorSignsLD || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </FormRow>
                <FormRow label="Will Agent sign the L&D etc. obo the Executor(s)?">
                   <Checkbox name="agentSignsLD" checked={formData.agentSignsLD || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </FormRow>
            </Box>

          </Stack>
        </TabPanel>

        {/* ================= TAB 3: EXECUTOR (CONT) ================= */}
        <TabPanel value={tabValue} index={2}>
            <Stack spacing={0.5}>
                
                <FormRow label="Will Executor sign Inventory/Affidavits?" required>
                   <Checkbox name="executorSignsInventory" checked={formData.executorSignsInventory !== false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </FormRow>

                <FormRow label="If not the Executor then who will?">
                    <TextField name="whoSignsInventory" value={formData.whoSignsInventory || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Capacity of person signing Inventory">
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                         <TextField name="capacitySignatory" value={formData.capacitySignatory || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                         <Button variant="outlined" size="small" sx={{ minWidth: '30px', px: 1 }}>...</Button>
                    </Stack>
                </FormRow>

                <FormRow label="Address of person signing Inventory">
                    <TextField name="addressSignatory" value={formData.addressSignatory || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1, display: 'block' }}>
                    NOTE: Do not use the fields below to generate document if the trust is an heir in this estate. Load the trust in the heirs tab.
                </Typography>

                <FormRow label="Name of Trust to be formed (if applicable)">
                    <TextField name="trustName" value={formData.trustName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Full names of the First Trustee">
                    <TextField name="trustee1FullName" value={formData.trustee1FullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Identity Number of the First Trustee">
                     <TextField name="trustee1ID" value={formData.trustee1ID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <FormRow label="Full names of the Second Trustee">
                    <TextField name="trustee2FullName" value={formData.trustee2FullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Identity Number of the Second Trustee">
                     <TextField name="trustee2ID" value={formData.trustee2ID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <FormRow label="Full names of the Third Trustee">
                    <TextField name="trustee3FullName" value={formData.trustee3FullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Identity Number of the Third Trustee">
                     <TextField name="trustee3ID" value={formData.trustee3ID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <Divider sx={{ my: 2 }} />

                <FormRow label="Name of Principal if working as assistant">
                    <TextField name="principalName" value={formData.principalName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

            </Stack>
        </TabPanel>

        {/* ================= TAB 4: CO-EXECUTORS ================= */}
        <TabPanel value={tabValue} index={3}>
            <Stack spacing={0.5}>
                
                {/* 1st Co-Executor Block */}
                <Box sx={{ mb: 4 }}>
                    <FormRow label="1st Co-Executor's Full Names" required>
                        <TextField name="coExecutor1FullName" value={formData.coExecutor1FullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fffde7', '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="1st Co-Executor's Identity Number">
                        <TextField name="coExecutor1ID" value={formData.coExecutor1ID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>
                    <FormRow label="1st Co-Executor's Home Address (if applic.)">
                        <TextField name="coExecutor1HomeAddress" value={formData.coExecutor1HomeAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="1st Co-Executor's Home Tel. No. (if applic.)">
                         <TextField name="coExecutor1HomePhone" value={formData.coExecutor1HomePhone || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>
                    <FormRow label="1st Co-Exec. Business Tel">
                         <TextField name="coExecutor1BusinessPhone" value={formData.coExecutor1BusinessPhone || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>
                    <FormRow label="1st Co-Executor's Business Address">
                        <TextField name="coExecutor1BusinessAddress" value={formData.coExecutor1BusinessAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="1st Co-Executor's Domicilium Address">
                        <TextField name="coExecutor1Domicilium" value={formData.coExecutor1Domicilium || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="1st Co-Executor's relationship to deceased">
                        <TextField name="coExecutor1Relationship" value={formData.coExecutor1Relationship || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* 2nd Co-Executor Block */}
                <Box sx={{ mb: 4 }}>
                    <FormRow label="2nd Co-Executor's Full Names">
                        <TextField name="coExecutor2FullName" value={formData.coExecutor2FullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="2nd Co-Executor's Identity Number">
                        <TextField name="coExecutor2ID" value={formData.coExecutor2ID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>
                    <FormRow label="2nd Co-Executor's Home Address (if applic.)">
                        <TextField name="coExecutor2HomeAddress" value={formData.coExecutor2HomeAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="2nd Co-Executor's Home Tel. No. (if applic.)">
                         <TextField name="coExecutor2HomePhone" value={formData.coExecutor2HomePhone || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>
                    <FormRow label="2nd Co-Exec. Business Tel">
                         <TextField name="coExecutor2BusinessPhone" value={formData.coExecutor2BusinessPhone || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>
                    <FormRow label="2nd Co-Executor's Business Address">
                        <TextField name="coExecutor2BusinessAddress" value={formData.coExecutor2BusinessAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="2nd Co-Executor's Domicilium Address">
                        <TextField name="coExecutor2Domicilium" value={formData.coExecutor2Domicilium || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                    <FormRow label="2nd Co-Executor's relationship to deceased">
                        <TextField name="coExecutor2Relationship" value={formData.coExecutor2Relationship || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                </Box>

            </Stack>
        </TabPanel>

        {/* ================= TAB 5: CO-EXECUTORS (CONT) ================= */}
        <TabPanel value={tabValue} index={4}>
            <Stack spacing={0.5}>
                
                {/* 3rd Co-Executor Block */}
                <Box sx={{ mb: 4 }}>
                    <FormRow label="3rd Co-Executor's Full Names" required>
                        <TextField name="coExecutor3FullName" value={formData.coExecutor3FullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fffde7', '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>

                    <FormRow label="3rd Co_Executor's Identity Number">
                        <TextField name="coExecutor3ID" value={formData.coExecutor3ID || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>

                    <FormRow label="3rd Co-Executor's Home Address (if applic.)">
                        <TextField name="coExecutor3HomeAddress" value={formData.coExecutor3HomeAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>

                    <FormRow label="3rd Co-Executor's Home Tel. No. (if applic.)">
                         <TextField name="coExecutor3HomePhone" value={formData.coExecutor3HomePhone || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>

                    <FormRow label="3rd Co-Exec. Business Tel">
                         <TextField name="coExecutor3BusinessPhone" value={formData.coExecutor3BusinessPhone || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                    </FormRow>

                    <FormRow label="3rd Co-Executor's Business Address">
                        <TextField name="coExecutor3BusinessAddress" value={formData.coExecutor3BusinessAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>

                    <FormRow label="3rd Co-Executor's Domicilium Address">
                        <TextField name="coExecutor3Domicilium" value={formData.coExecutor3Domicilium || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>

                    <FormRow label="3rd Co-Executor's relationship to deceased">
                        <TextField name="coExecutor3Relationship" value={formData.coExecutor3Relationship || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                    </FormRow>
                </Box>

            </Stack>
        </TabPanel>

        {/* ================= TAB 6: DECEASED ================= */}
        <TabPanel value={tabValue} index={5}>
            <Stack spacing={0.5}>
                
                <FormRow label="Deceased's Full First Names" required>
                    <TextField name="deceasedFirstNames" value={formData.deceasedFirstNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fffde7', '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Deceased's present Surname">
                    <TextField name="deceasedSurname" value={formData.deceasedSurname || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                {/* Split Row: Maiden Name | Previous Surnames */}
                <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Deceased's maiden name (if applic)" customGrid={[12, 12, 4, 8]}>
                            <TextField name="deceasedMaidenName" value={formData.deceasedMaidenName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                        </FormRow>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Previous surname(s)" customGrid={[12, 12, 3, 9]}>
                            <TextField name="deceasedPreviousSurnames" value={formData.deceasedPreviousSurnames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                        </FormRow>
                    </Grid>
                </Grid>

                {/* Split Row: ID | Gender */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Deceased's Identity Number" customGrid={[12, 12, 4, 8]}>
                             <TextField name="deceasedID" value={formData.deceasedID || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                        </FormRow>
                    </Grid>
                    <Grid item xs={12} md={6}>
                         <Grid container alignItems="center">
                            <Grid item xs={3}><Typography variant="subtitle2">Deceased's Gender</Typography></Grid>
                            <Grid item xs={9}>
                                <RadioGroup row name="deceasedGender" value={formData.deceasedGender || 'Male'} onChange={handleChange}>
                                    <FormControlLabel value="Male" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Male</Typography>} />
                                    <FormControlLabel value="Female" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Female</Typography>} />
                                </RadioGroup>
                            </Grid>
                         </Grid>
                    </Grid>
                </Grid>

                {/* Split Row: DOB | Place of Birth */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Deceased's Date of Birth" customGrid={[12, 12, 4, 8]}>
                             <TextField name="deceasedDOB" value={formData.deceasedDOB || ''} onChange={handleChange} sx={{ width: '120px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" placeholder="DD/MM/YYYY" />
                        </FormRow>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Deceased's Place of Birth" customGrid={[12, 12, 3, 9]}>
                            <TextField name="deceasedPlaceOfBirth" value={formData.deceasedPlaceOfBirth || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                        </FormRow>
                    </Grid>
                </Grid>

                {/* Split Row: DOD | Place of Death */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Deceased's Date of Death" customGrid={[12, 12, 4, 8]}>
                             <TextField name="deceasedDOD" value={formData.deceasedDOD || ''} onChange={handleChange} sx={{ width: '120px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                        </FormRow>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormRow label="Deceased's Place of Death" customGrid={[12, 12, 3, 9]}>
                            <TextField name="deceasedPlaceOfDeath" value={formData.deceasedPlaceOfDeath || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                        </FormRow>
                    </Grid>
                </Grid>

                <FormRow label="Deceased's Income Tax Number">
                    <TextField name="deceasedIncomeTaxNo" value={formData.deceasedIncomeTaxNo || ''} onChange={handleChange} sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <Divider sx={{ my: 2, borderStyle: 'solid', borderColor: '#f0f0f0' }} />

                {/* MARITAL STATUS SECTION */}
                <Box sx={{ bgcolor: '#f9f9f9', p: 2, border: '1px solid #eee', borderRadius: 1, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="subtitle2">Indicate Deceased's Marital Status</Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <RadioGroup row name="deceasedMaritalStatus" value={formData.deceasedMaritalStatus || ''} onChange={handleChange}>
                                <Grid container>
                                    <Grid item xs={4}>
                                        <FormControlLabel value="Community" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Married in community of property</Typography>} />
                                        <FormControlLabel value="OutCommunity" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Married out of community of property</Typography>} />
                                        <FormControlLabel value="Accrual" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Married out of community of property with accrual</Typography>} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControlLabel value="Unmarried" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Unmarried</Typography>} />
                                        <FormControlLabel value="Widowed" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Widowed</Typography>} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControlLabel value="Divorced" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Divorced</Typography>} />
                                        <FormControlLabel value="Other" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="body2">Other</Typography>} />
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                        </Grid>
                    </Grid>
                </Box>

                <FormRow label="If &quot;Other&quot; complete marital description">
                    <TextField name="deceasedOtherMaritalStatus" value={formData.deceasedOtherMaritalStatus || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Spouse's Full Names and Surname">
                    <TextField name="spouseFullName" value={formData.spouseFullName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Spouse's maiden name (if applic)">
                    <TextField name="spouseMaidenName" value={formData.spouseMaidenName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Spouse's previous surname(s)">
                    <TextField name="spousePreviousSurnames" value={formData.spousePreviousSurnames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Address of Deceased's Spouse">
                    <TextField name="spouseAddress" value={formData.spouseAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Occupation of Deceased's Spouse">
                    <TextField name="spouseOccupation" value={formData.spouseOccupation || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Spouse's Identity Number">
                     <TextField name="spouseID" value={formData.spouseID || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <FormRow label="Spouse's Date of Birth">
                     <TextField type="date" InputLabelProps={{ shrink: true }} name="spouseDOB" value={formData.spouseDOB || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" inputProps={{ max: '9999-12-31' }} />
                </FormRow>

                <FormRow label="Deceased's Place of Marriage">
                    <TextField name="placeOfMarriage" value={formData.placeOfMarriage || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Deceased's Date of Marriage">
                     <TextField type="date" InputLabelProps={{ shrink: true }} name="dateOfMarriage" value={formData.dateOfMarriage || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" inputProps={{ max: '9999-12-31' }} />
                </FormRow>

            </Stack>
        </TabPanel>

{/* ================= TAB 7: DECEASED (CONT) / WILL ================= */}
        <TabPanel value={tabValue} index={6}>
            <Stack spacing={0.5}>

                <FormRow label="Deceased's Last known Address">
                    <TextField name="lastKnownAddress" value={formData.lastKnownAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="District in which Deceased Resided">
                    <TextField name="districtResided" value={formData.districtResided || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Was Deceased ordinarily resident in SA?">
                    <Checkbox name="ordinarilyResidentSA" checked={formData.ordinarilyResidentSA !== false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </FormRow>

                <FormRow label="If not, where was he domiciled?">
                    <TextField name="domiciledWhere" value={formData.domiciledWhere || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <Divider sx={{ my: 2, borderStyle: 'solid', borderColor: '#f0f0f0' }} />

                <FormRow label="Date of Deceased's Will:">
                    <TextField type="date" InputLabelProps={{ shrink: true }} name="dateOfWill" value={formData.dateOfWill || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" inputProps={{ max: '9999-12-31' }} />
                </FormRow>

                <FormRow label="Date(s) of Codicil(s)">
                    <TextField name="dateOfCodicils" value={formData.dateOfCodicils || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Place where Will was Signed">
                    <TextField name="placeWillSigned" value={formData.placeWillSigned || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Place Codicil(s) signed">
                    <TextField name="placeCodicilsSigned" value={formData.placeCodicilsSigned || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <Divider sx={{ my: 2, borderStyle: 'solid', borderColor: '#f0f0f0' }} />

                <FormRow label="Redistribution Agreement: signed at:">
                    <TextField name="redistributionAgreementSignedAt" value={formData.redistributionAgreementSignedAt || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Redistribution Agreement, date signed:">
                    <TextField type="date" InputLabelProps={{ shrink: true }} name="redistributionAgreementDate" value={formData.redistributionAgreementDate || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" inputProps={{ max: '9999-12-31' }} />
                </FormRow>

                <Divider sx={{ my: 2, borderStyle: 'solid', borderColor: '#f0f0f0' }} />

                <FormRow label="Names of Deceased's Father">
                    <TextField name="fatherNames" value={formData.fatherNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Names of Deceased's Mother">
                    <TextField name="motherNames" value={formData.motherNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="What was the Deceased's nationality?">
                    <TextField name="nationality" value={formData.nationality || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Deceased's occupation">
                    <TextField name="occupation" value={formData.occupation || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Deceased's children (state if major or minor & complete addresses) used for DEATH NOTICE only. Next-of-Kin Affidavit details come from the info completed in the heirs' screens.">
                     <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                        <TextField 
                            name="childrenDetails" value={formData.childrenDetails || ''} onChange={handleChange}
                            fullWidth 
                            multiline 
                            rows={4} 
                            size="small" 
                            variant="outlined" 
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }} 
                        />
                        <Button variant="outlined" sx={{ minWidth: '40px', height: '40px' }}>...</Button>
                     </Stack>
                </FormRow>

                <FormRow label="Names of predeceased/divorce spouse(s)">
                    <TextField name="predeceasedSpouseNames" value={formData.predeceasedSpouseNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </FormRow>

                <FormRow label="Which Master dealt with the estate?">
                     <TextField name="masterDealtWithEstate" value={formData.masterDealtWithEstate || ''} onChange={handleChange} sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <FormRow label="Date of death/divorce">
                     <TextField name="dateOfDeathDivorce" value={formData.dateOfDeathDivorce || ''} onChange={handleChange} sx={{ width: '300px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </FormRow>

                <Box sx={{ mt: 2 }}>
                    <FormRow label="Did the Deceased have a responsibility as Director or Trustee that still has to be transferred?">
                        <Checkbox name="directorTrusteeResponsibility" checked={formData.directorTrusteeResponsibility || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </FormRow>
                </Box>

            </Stack>
        </TabPanel>

        {/* ================= TAB 8: HEIRS ================= */}
        <TabPanel value={tabValue} index={7}>
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                
                {/* Table Container */}
                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', mb: 2 }}>
                    <Table size="small" sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.200' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Full Name<br/>and<br/>Surname</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Identity<br/>Number</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date<br/>of<br/>Birth</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Relationship<br/>to<br/>Deceased</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Details<br/>of<br/>Inheritance</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Residue<br/>Portion</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Cash<br/>Legacy</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Cell No</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Email<br/>Address</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '120px' }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                                {(formData.heirs || []).map((heir, index) => (
                                    <TableRow key={heir.id || index} sx={{ height: 35 }}>
                                        <TableCell>{heir.firstNames} {heir.surname}</TableCell>
                                        <TableCell>{heir.idNumber}</TableCell>
                                        <TableCell>{heir.dob}</TableCell>
                                        <TableCell>{heir.relationship}</TableCell>
                                        <TableCell>{/* Details of Inheritance */}</TableCell>
                                        <TableCell>{heir.residueFractionNumerator}/{heir.residueFractionDenominator}</TableCell>
                                        <TableCell>{formatCurrency(heir.cashLegacyAmount)}</TableCell>
                                        <TableCell>{/* Cell No */}</TableCell>
                                        <TableCell>{/* Email */}</TableCell>
                                        <TableCell>{heir.address}</TableCell>
                                        <TableCell>
                                            <Button size="small" color="primary" onClick={() => handleEditItem(heir, 'heir')} sx={{ minWidth: '40px', p: 0, mr: 1 }}>Edit</Button>
                                            <Button size="small" color="error" onClick={() => handleDelete('heirs', heir.id)} sx={{ minWidth: '30px', p: 0 }}>X</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(formData.heirs || []).length === 0 && (
                                    <TableRow><TableCell colSpan={11} align="center">No heirs added</TableCell></TableRow>
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Main Action Buttons */}
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                    {/* ADDED onClick HANDLER HERE */}
                    <Button 
                        onClick={handleOpenHeirDialog}
                        variant="contained" 
                        color="inherit" 
                        startIcon={<span>+</span>} 
                        sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }}
                    >
                        Insert
                    </Button>
                    <Button variant="contained" color="inherit" startIcon={<span>△</span>} sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }} disabled>
                        Change
                    </Button>
                    <Button variant="contained" color="inherit" startIcon={<span>-</span>} sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }} disabled>
                        Delete
                    </Button>
                    <Button variant="contained" color="inherit" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }}>
                        Order
                    </Button>
                </Stack>

                {/* Footer Links & Notes */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', background: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }} />
                        <Typography variant="body2" sx={{ textDecoration: 'underline', color: 'primary.main', cursor: 'pointer' }}>
                            Allocate residue fractions
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ textDecoration: 'underline', color: 'primary.main', cursor: 'pointer' }}>
                        Update advances from cashbook
                    </Typography>
                </Stack>

                <Grid container spacing={2}>
                    <Grid item xs={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>NOTE:</Typography>
                    </Grid>
                    <Grid item xs={11}>
                        <Typography variant="body2" display="block">
                            Even if a person/entity only gets a cash legacy/special bequest or the bare dominium of an asset from a previous estate, they must still be entered here as heirs. This will allow you to award assets/liabilities to them and/or to calculate the estate duty value of the right now ceasing.
                        </Typography>
                    </Grid>
                </Grid>

                {/* RENDER THE DIALOG COMPONENT HERE */}
                <AddHeirDialog 
                    open={openHeirDialog} 
                    onClose={() => { setOpenHeirDialog(false); setEditData(null); }}
                    matterId={id}
                    onSave={handleRefetch}
                    initialData={editData}
                />

            </Box>
        </TabPanel>

        {/* ================= TAB 9: IMMOVABLES ================= */}
        <TabPanel value={tabValue} index={8}>
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                
                {/* Table Container */}
                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', mb: 2 }}>
                    <Table size="small" sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.200' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Description<br/>of<br/>Immovable Asset</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Inventory<br/>value</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>L&D<br/>value</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>How<br/>dealt<br/>with</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Will<br/>Clause</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Realised<br/>Value</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '120px' }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                                {[
                                    ...(formData.conventionalProperties || []).map(p => ({ ...p, type: 'Conventional', subPath: 'conventional-properties' })),
                                    ...(formData.sectionalProperties || []).map(p => ({ ...p, type: 'Sectional', subPath: 'sectional-properties' }))
                                ].map((asset, index) => (
                                    <TableRow key={asset.id || index} sx={{ height: 35 }}>
                                        <TableCell>{asset.description}</TableCell>
                                        <TableCell>{formatCurrency(asset.inventoryValue)}</TableCell>
                                        <TableCell>{formatCurrency(asset.ldValue)}</TableCell>
                                        <TableCell>{asset.awardedTo}</TableCell>
                                        <TableCell>{/* Notes */}</TableCell>
                                        <TableCell>{asset.willClause}</TableCell>
                                        <TableCell>{/* Realised Value */}</TableCell>
                                        <TableCell>
                                            <Button size="small" color="primary" onClick={() => handleEditItem(asset, asset.type === 'Conventional' ? 'conventional' : 'sectional')} sx={{ minWidth: '40px', p: 0, mr: 1 }}>Edit</Button>
                                            <Button size="small" color="error" onClick={() => handleDelete(asset.subPath, asset.id)} sx={{ minWidth: '30px', p: 0 }}>X</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!formData.conventionalProperties?.length && !formData.sectionalProperties?.length) && (
                                    <TableRow><TableCell colSpan={8} align="center">No immovable assets added</TableCell></TableRow>
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Main Action Buttons */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
                    <Box sx={{ width: '100px' }} />
                    
                    <Stack direction="row" spacing={2}>
                        <Button 
                            onClick={handleOpenPropertyTypeDialog} 
                            variant="contained" 
                            color="inherit" 
                            startIcon={<span>+</span>} 
                            sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }}
                        >
                            Insert
                        </Button>
                        <Button variant="contained" color="inherit" startIcon={<span>△</span>} sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }} disabled>
                            Change
                        </Button>
                        <Button variant="contained" color="inherit" startIcon={<span>-</span>} sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }} disabled>
                            Delete
                        </Button>
                        <Button variant="contained" color="inherit" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }}>
                            Order
                        </Button>
                    </Stack>

                    <Button sx={{ minWidth: '40px', color: '#000' }}>
                        <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>&lt;</span>
                    </Button>
                </Stack>

                <Grid container spacing={2}>
                    <Grid item xs={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>NOTE:</Typography>
                    </Grid>
                    <Grid item xs={11}>
                        <Typography variant="body2" display="block">
                            To record property over which the deceased had no testamentary capacity (like fiduciary interests), add the property here and then click on the "42(2)/Rights" button to record the and calculate the values. Any such assets will not be included as the deceased's assets.
                        </Typography>
                    </Grid>
                </Grid>

                {/* --- PROPERTY TYPE SELECTION MODAL --- */}
                <Dialog 
                    open={openPropertyTypeDialog} 
                    onClose={handleClosePropertyTypeDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 1, boxShadow: 3, border: '1px solid #ccc' } }}
                >
                    <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #eee', bgcolor: '#f9f9f9' }}>
                         <Typography variant="subtitle2">LegalEase Admin of Deceased Estates</Typography>
                    </Box>

                    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 2 }}>
                             <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>?</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            What type of property do you want to insert?
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ pb: 3, px: 2 }}>
                        {/* UPDATE: This button now opens the large dialog */}
                        <Button 
                            onClick={handleOpenConventionalDialog} 
                            variant="outlined" 
                            size="small"
                            sx={{ textTransform: 'none', minWidth: '90px', color: '#333', borderColor: '#999', fontWeight: 'bold', border: '2px solid #1976d2' }}
                        >
                            Conventional
                        </Button>
                        <Button 
    onClick={handleOpenSectionalDialog}
    variant="outlined" 
    size="small"
    sx={{ textTransform: 'none', minWidth: '90px', color: '#333', borderColor: '#ccc', bgcolor: '#f0f0f0' }}
>
                            Sectional
                        </Button>
                        <Button 
                            onClick={handleClosePropertyTypeDialog}
                            variant="outlined" 
                            size="small"
                            sx={{ textTransform: 'none', minWidth: '90px', color: '#333', borderColor: '#ccc', bgcolor: '#f0f0f0' }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Dialog>

                {/* --- NEW CONVENTIONAL PROPERTY MODAL --- */}
                <AddConventionalPropertyDialog 
                    open={openConventionalDialog} 
                    onClose={() => { setOpenConventionalDialog(false); setEditData(null); }}
                    matterId={id}
                    onSave={handleRefetch}
                    initialData={editData}
                />

                <AddSectionalPropertyDialog  
                open={openSectionalDialog} 
                onClose={() => { setOpenSectionalDialog(false); setEditData(null); }}
                matterId={id}
                onSave={handleRefetch}
                initialData={editData}
                />

            </Box>
        </TabPanel>

       {/* ================= TAB 10: MOVABLES ================= */}
<TabPanel value={tabValue} index={9}>
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Data Grid Container */}
        <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', mb: 2 }}>
            <Table size="small" sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type<br/>of<br/>Asset</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description<br/>of<br/>Movable or Shares</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Inventory<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>L&D<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>How<br/>dealt<br/>with</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Will<br/>Clause</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Realised<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '120px' }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                        {[
                            ...(formData.movableAssets || []).map(m => ({ ...m.data, id: m.id, assetType: 'Movable', subPath: 'movable-assets' })),
                            ...(formData.sharesAssets || []).map(s => ({ ...s.data, id: s.id, assetType: 'Shares', subPath: 'shares-assets' }))
                        ].map((asset, index) => (
                            <TableRow key={asset.id || index} sx={{ height: 30 }}>
                                <TableCell>{asset.assetType}</TableCell>
                                <TableCell>{asset.description}</TableCell>
                                <TableCell>{formatCurrency(asset.inventoryValue)}</TableCell>
                                <TableCell>{formatCurrency(asset.ldValue)}</TableCell>
                                <TableCell>{asset.awardedTo}</TableCell>
                                <TableCell>{asset.notes}</TableCell>
                                <TableCell>{asset.willClause}</TableCell>
                                <TableCell>{formatCurrency(asset.realisedValue)}</TableCell>
                                <TableCell>
                                    <Button size="small" color="primary" onClick={() => handleEditItem(asset, asset.assetType === 'Movable' ? 'movable' : 'shares')} sx={{ minWidth: '40px', p: 0, mr: 1 }}>Edit</Button>
                                    <Button size="small" color="error" onClick={() => handleDelete(asset.subPath, asset.id)} sx={{ minWidth: '30px', p: 0 }}>X</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!formData.movableAssets?.length && !formData.sharesAssets?.length) && (
                            <TableRow><TableCell colSpan={9} align="center">No movable assets added</TableCell></TableRow>
                        )}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Action Buttons Row */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 4 }}>
            <Button 
                onClick={handleOpenMovableTypeDialog}
                variant="contained" 
                color="inherit" 
                startIcon={<span style={{ fontSize: '1.2rem' }}>+</span>} 
                sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}
            >
                Insert
            </Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Change</Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Delete</Button>
            <Button variant="contained" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0', ml: 4 }}>Order</Button>
        </Stack>

        {/* Footer Note Section */}
        <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
            <Grid item xs={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>NOTE:</Typography>
            </Grid>
            <Grid item xs={11}>
                <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4 }}>
                    Only ordinary movables and REGISTERED shares must be inserted here. Stock and unit
                    trusts held must be inserted as an ordinary movable. Income/expenses after death must be
                    captured in the Cashbook and linked to the asset from there.
                </Typography>
            </Grid>
        </Grid>

        {/* --- 1. SELECTION MODAL --- */}
        <Dialog 
            open={openMovableTypeDialog} 
            onClose={handleCloseMovableTypeDialog}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 0, border: '1px solid #000', bgcolor: '#f0ffff' } }}
        >
            <Box sx={{ px: 1, py: 0.5, bgcolor: '#fff', borderBottom: '1px solid #ccc' }}>
                <Typography variant="subtitle2">LegalEase Deceased Estates</Typography>
            </Box>
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Select the type of movable you want to insert</Typography>
            </Box>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ pb: 3, px: 2 }}>
                <Button onClick={handleOpenMovableDetails} variant="contained" size="small" sx={{ textTransform: 'none', minWidth: '90px', color: '#000', bgcolor: '#e0e0e0', border: '1px solid #1976d2' }}>Movable</Button>
                {/* UPDATED: Added handleOpenSharesDetails here */}
                <Button onClick={handleOpenSharesDetails} variant="contained" size="small" sx={{ textTransform: 'none', minWidth: '90px', color: '#000', bgcolor: '#e0e0e0', border: '1px solid #999' }}>Shares</Button>
                <Button onClick={handleCloseMovableTypeDialog} variant="contained" size="small" sx={{ textTransform: 'none', minWidth: '90px', color: '#000', bgcolor: '#e0e0e0', border: '1px solid #999' }}>Cancel</Button>
            </Stack>
        </Dialog>

        {/* --- 2. MOVABLE ENTRY MODAL --- */}
        <AddMovableAssetDialog 
            open={openMovableDetails} 
            onClose={() => { setOpenMovableDetails(false); setEditData(null); }} 
            matterId={id}
            onSave={handleRefetch}
            initialData={editData}
        />

        {/* --- 3. SHARES ENTRY MODAL --- */}
        <AddSharesAssetDialog 
            open={openSharesDetails} 
            onClose={() => { setOpenSharesDetails(false); setEditData(null); }} 
            matterId={id}
            onSave={handleRefetch}
            initialData={editData}
        />
    </Box>
</TabPanel>

{/* ================= TAB 11: CLAIMS/POLICIES ================= */}
<TabPanel value={tabValue} index={10}>
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
        
        {/* Data Grid Container */}
        <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', mb: 2 }}>
            <Table size="small" sx={{ minWidth: 1100, tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type<br/>of<br/>claim</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description<br/>of<br/>Claim or Policy</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Inventory<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>L&D<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>How<br/>dealt<br/>with</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Cashbook<br/>linked<br/>to</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Will<br/>Clause</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Realised<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Protection<br/>insurance</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '120px' }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                        {[
                            ...(formData.claims || []).map(c => ({ ...c, type: 'Claim', subPath: 'claims' })),
                            ...(formData.policies || []).map(p => ({ ...p, type: 'Policy', subPath: 'policies' }))
                        ].map((item, index) => (
                            <TableRow key={item.id || index} sx={{ height: 30 }}>
                                <TableCell>{item.type}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>{formatCurrency(item.inventoryValue)}</TableCell>
                                <TableCell>{formatCurrency(item.ldValue || item.sarsValue)}</TableCell>
                                <TableCell>{item.awardedTo || item.beneficiaryName}</TableCell>
                                <TableCell>{/* Cashbook linked */}</TableCell>
                                <TableCell>{item.notes}</TableCell>
                                <TableCell>{item.willClause}</TableCell>
                                <TableCell>{formatCurrency(item.proceeds)}</TableCell>
                                <TableCell>{/* Protection insurance */}</TableCell>
                                <TableCell>
                                    <Button size="small" color="primary" onClick={() => handleEditItem(item, item.type === 'Claim' ? 'claim' : 'policy')} sx={{ minWidth: '40px', p: 0, mr: 1 }}>Edit</Button>
                                    <Button size="small" color="error" onClick={() => handleDelete(item.subPath, item.id)} sx={{ minWidth: '30px', p: 0 }}>X</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!formData.claims?.length && !formData.policies?.length) && (
                            <TableRow><TableCell colSpan={11} align="center">No claims or policies added</TableCell></TableRow>
                        )}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Action Buttons Row */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 4 }}>
            <Button 
                onClick={handleOpenEntryTypeDialog} // UPDATED: Trigger selection dialog
                variant="contained" 
                color="inherit" 
                startIcon={<span style={{ fontSize: '1.2rem' }}>+</span>} 
                sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}
            >
                Insert
            </Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Change</Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Delete</Button>
            <Button variant="contained" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0', ml: 4 }}>Order</Button>
        </Stack>

        {/* Footer Note Section */}
        <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
            <Grid item xs={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>NOTE:</Typography>
            </Grid>
            <Grid item xs={11}>
                <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4, mb: 1 }}>
                    Claims in favour of the estate include insurance policies, current and savings accounts, fixed deposits,
                    participation bonds, cash, dividends accrued before, but payable after death etc.
                </Typography>
                <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4 }}>
                    Claims against the estate must be inserted under "Liabilities".
                </Typography>
            </Grid>
        </Grid>

        {/* --- ENTRY TYPE SELECTION MODAL --- */}
        <Dialog 
            open={openEntryTypeDialog} 
            onClose={handleCloseEntryTypeDialog}
            maxWidth="xs"
            fullWidth
            PaperProps={{ 
                sx: { 
                    borderRadius: 1, 
                    boxShadow: 3, 
                    border: '1px solid #ccc',
                    bgcolor: '#f0f0f0' 
                } 
            }}
        >
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #eee', bgcolor: '#f9f9f9' }}>
                 <Typography variant="subtitle2">LegalEase Admin of Deceased Estates</Typography>
            </Box>

            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                    width: 40, height: 40, borderRadius: '50%', bgcolor: '#1976d2', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                    flexShrink: 0, boxShadow: 2 
                }}>
                     <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>?</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Select the type of entry you want to insert
                </Typography>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="center" sx={{ pb: 3, px: 2 }}>
    <Button 
        onClick={handleClaimInFavourSelected} // FIXED: Linking the unused variable
        variant="outlined" 
        size="small"
        sx={{ 
            textTransform: 'none', 
            minWidth: '110px', 
            color: '#333', 
            borderColor: '#999', 
            fontWeight: 'bold', 
            border: '2px solid #1976d2' 
        }}
    >
        Claim in favour
    </Button>
    <Button 
        onClick={handlePolicySelected} // FIXED: Linking the unused variable
        variant="outlined" 
        size="small"
        sx={{ 
            textTransform: 'none', 
            minWidth: '90px', 
            color: '#333', 
            borderColor: '#ccc', 
            bgcolor: '#f0f0f0' 
        }}
    >
        Policy
    </Button>
    <Button 
        onClick={handleCloseEntryTypeDialog}
        variant="outlined" 
        size="small"
        sx={{ 
            textTransform: 'none', 
            minWidth: '90px', 
            color: '#333', 
            borderColor: '#ccc', 
            bgcolor: '#f0f0f0' 
        }}
    >
        Cancel
    </Button>
</Stack>
        </Dialog>

        <AddClaimDialog 
            open={openClaimDialog} 
            onClose={() => { setOpenClaimDialog(false); setEditData(null); }} 
            matterId={id}
            onSave={handleRefetch}
            initialData={editData}
        />
        <AddPolicyDialog 
            open={openPolicyDialog} 
            onClose={() => { setOpenPolicyDialog(false); setEditData(null); }} 
            matterId={id}
            onSave={handleRefetch}
            initialData={editData}
        />
    </Box>
</TabPanel>

{/* ================= TAB 12: LIABILITIES ================= */}
<TabPanel value={tabValue} index={11}>
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
        
        {/* Data Grid Container */}
        <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', mb: 2 }}>
            <Table size="small" sx={{ minWidth: 1100, tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type<br/>of<br/>claim</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description<br/>of<br/>liability</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Inventory<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>L&D<br/>Value</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>How<br/>dealt<br/>with</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Cashbook</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Realised</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Covered by<br/>credit<br/>insurance?</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '120px' }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                        {(formData.liabilities || []).map(l => ({ ...l.data, id: l.id, subPath: 'liabilities' })).map((item, index) => (
                            <TableRow key={item.id || index} sx={{ height: 30 }}>
                                <TableCell>{item.type}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>{formatCurrency(item.inventoryValue)}</TableCell>
                                <TableCell>{formatCurrency(item.ldValue)}</TableCell>
                                <TableCell>{/* How dealt with */}</TableCell>
                                <TableCell>{/* Cashbook */}</TableCell>
                                <TableCell>{item.notes}</TableCell>
                                <TableCell>{/* Realised */}</TableCell>
                                <TableCell>{item.creditInsurance ? 'Yes' : 'No'}</TableCell>
                                <TableCell>
                                    <Button size="small" color="primary" onClick={() => handleEditItem(item, 'liability')} sx={{ minWidth: '40px', p: 0, mr: 1 }}>Edit</Button>
                                    <Button size="small" color="error" onClick={() => handleDelete(item.subPath, item.id)} sx={{ minWidth: '30px', p: 0 }}>X</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!formData.liabilities || formData.liabilities.length === 0) && (
                            <TableRow><TableCell colSpan={10} align="center">No liabilities added</TableCell></TableRow>
                        )}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Action Buttons Row */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 4 }}>
            <Button 
                onClick={() => { setEditData(null); setOpenLiabilityDialog(true); }}
                variant="contained" 
                color="inherit" 
                startIcon={<span style={{ fontSize: '1.2rem' }}>+</span>} 
                sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}
            >
                Insert
            </Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Change</Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Delete</Button>
            <Button variant="contained" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0', ml: 4 }}>Order</Button>
        </Stack>

        {/* Footer Note Section */}
        <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
            <Grid item xs={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>NOTE:</Typography>
            </Grid>
            <Grid item xs={11}>
                <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4, mb: 1 }}>
                    Liabilities include claims against the estate and administration costs.
                </Typography>
                <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4 }}>
                    Advert costs, fees, postage & petties are NOT inserted here. Insert it in the L&D a/c button above.
                </Typography>
            </Grid>
        </Grid>

        <AddLiabilityDialog 
            open={openLiabilityDialog} 
            onClose={() => { setOpenLiabilityDialog(false); setEditData(null); }} 
            matterId={id}
            onSave={handleRefetch}
            initialData={editData}
        />
    </Box>
</TabPanel>

{/* ================= TAB 13: BOND OF SECURITY ================= */}
<TabPanel value={tabValue} index={12}>
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
        
        {/* Data Grid Container */}
        <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', mb: 2 }}>
            <Table size="small" sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name<br/>of<br/>Surety</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Surety's<br/>Address</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Surety<br/>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '120px' }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                        {(formData.bondsOfSecurity || []).map((bond, index) => (
                            <TableRow key={bond.id || index} sx={{ height: 30 }}>
                                <TableCell>{bond.suretyName}</TableCell>
                                <TableCell>{bond.domiciliumAddress}</TableCell>
                                <TableCell>{formatCurrency(bond.amount)}</TableCell>
                                <TableCell>
                                    <Button size="small" color="primary" onClick={() => handleEditItem(bond, 'bond')} sx={{ minWidth: '40px', p: 0, mr: 1 }}>Edit</Button>
                                    <Button size="small" color="error" onClick={() => handleDelete('bonds', bond.id)} sx={{ minWidth: '30px', p: 0 }}>X</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!formData.bondsOfSecurity || formData.bondsOfSecurity.length === 0) && (
                            <TableRow><TableCell colSpan={4} align="center">No bonds of security added</TableCell></TableRow>
                        )}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Action Buttons Row */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 4 }}>
            <Button 
                onClick={() => { setEditData(null); setOpenBondDialog(true); }}
                variant="contained" 
                color="inherit" 
                startIcon={<span style={{ fontSize: '1.2rem' }}>+</span>} 
                sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}
            >
                Insert
            </Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Change</Button>
            <Button variant="contained" disabled sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '120px', border: '1px solid #b0b0b0' }}>Delete</Button>
        </Stack>

        <AddBondOfSecurityDialog 
            open={openBondDialog} 
            onClose={() => { setOpenBondDialog(false); setEditData(null); }} 
            matterId={id}
            onSave={handleRefetch}
            initialData={editData}
        />
    </Box>
</TabPanel>


        </Paper>
      </Container>

      {/* Save Button */}
      {!(tabValue >= 7 && tabValue <= 11) && (
      <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000 }}>
          <Button 
              variant="contained" 
              color="primary" 
              size="large" 
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, boxShadow: 3 }}
          >
              {saving ? 'Saving...' : 'Save Changes'}
          </Button>
      </Box>
      )}

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Page>
  );
}